import { alpha, Box, Grid, useTheme } from '@mui/material';
import { generateNodeId, getTaskColor } from '@utils/flows';
import { IS_TEST_MODE } from 'constants/global';
import React, { useCallback } from 'react';
import { NodeProps } from 'reactflow';
import {
	ACTION_NODE_WIDTH,
	ITaskNodeData,
	NextActionTaskType
} from 'types/flows';
import { ITheme } from 'types/theme';
import ActionableNodeTitle from './ActionableNodeTitle';
import ActionOutputBlock from './ActionOutputBlock';
import InputHandle from './InputHandle';
import useHandleConnectOnly from './useHandleConnectOnly';

const ActionableNode = ({
	data,
	id,
	selected,
	dragging
}: NodeProps<ITaskNodeData>) => {
	const { onEditTask, onDeleteTask, task } = data;
	const theme = useTheme<ITheme>();
	const onEditClick = useCallback(() => {
		if (task) onEditTask?.(task);
	}, [onEditTask, task]);

	const onDeleteClick = useCallback(() => {
		if (task) onDeleteTask?.(task);
	}, [task, onDeleteTask]);

	const color = getTaskColor(theme, data.task?.type);

	const [handleRef] = useHandleConnectOnly(id);

	return (
		<Box
			sx={{
				'&:hover': {
					'#node-options-button': {
						opacity: 1
					}
				},
				'#node-options-button': {
					opacity: 0,
					transition: 'opacity 0.2s'
				}
			}}
			// we add nodrag in tests as d3 does not support non browser interaction. source: https://github.com/wbkd/react-flow/issues/2461
			className={`actionable-node ${IS_TEST_MODE ? 'nodrag' : ''}`}
			data-testid={generateNodeId(id)}
			p={2}
			width={ACTION_NODE_WIDTH}
			boxShadow={selected ? `0px 0px 15px ${alpha(color, 0.25)}` : undefined}
			borderRadius={2}
			border={1}
			borderColor={data?.marked ? color : theme.palette.border.main}
			bgcolor={theme.palette.background.paper}
		>
			<ActionableNodeTitle
				color={color}
				title={data.task?.name || ''}
				onDeleteClick={onDeleteClick}
				onEditClick={onEditClick}
				taskType={data.task?.type!}
			/>
			<Grid
				container
				spacing={1}
				pt={2}
				data-testid="node-interactions-container"
			>
				{data.interactions?.map((interaction) => {
					// hide all error interactions.
					if (interaction.type === NextActionTaskType.error) {
						return null;
					}
					return (
						<Grid item xs={12} key={interaction.id}>
							<ActionOutputBlock
								parentId={id}
								interaction={interaction}
								selected={!!data?.marked}
							/>
						</Grid>
					);
				})}
			</Grid>
			{!dragging && (
				<InputHandle
					ref={handleRef as React.MutableRefObject<HTMLDivElement | null>}
				/>
			)}
		</Box>
	);
};

export default ActionableNode;
